import type { FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import * as FormDialog from "../../shared/FormDialog";
import type { CreatePartnerPayload } from "../../api/partners";
import { CreatePartnerErrorResponseSchema, createPartnerMutationOptions } from "../../api/partners";
import { useMutation } from "@tanstack/react-query";

type CreatePartnerDialogProps = {
  onClose: () => void;
  onCreated: (created: { id: string; name: string }) => void;
  defaultName?: string;
};

export const CreatePartnerDialog: FC<CreatePartnerDialogProps> = ({ onClose, onCreated, defaultName = "" }) => {
  const { t } = useTranslation();
  const createPartnerMutation = useMutation(createPartnerMutationOptions());
  const { handleSubmit, control, setFocus } = useForm<CreatePartnerPayload>({
    defaultValues: { name: defaultName },
  });

  FormDialog.useAutoFocusField(setFocus, "name");

  const handleNewPartner = ({ name }: { name: string }) =>
    createPartnerMutation.mutate(
      { name },
      {
        onSuccess: async ({ id }) => {
          onClose();
          onCreated({ id, name });
        },
      }
    );

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("CreatePartner.Dialog.Title")}</FormDialog.Title>

      <FormDialog.Form onSubmit={handleSubmit(handleNewPartner)}>
        <FormDialog.Content>
          <FormDialog.FormError
            isError={createPartnerMutation.isError}
            errorMessage={
              CreatePartnerErrorResponseSchema.safeParse(createPartnerMutation.error).success
                ? t("CreatePartner.Dialog.PartnerAlreadyExistsError")
                : undefined
            }
          />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="name"
              rules={{ required: t("CreatePartner.Dialog.Error") }}
              label={t("CreatePartner.Dialog.NameLabel")}
              placeholder={t("CreatePartner.Dialog.NamePlaceholder")}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={createPartnerMutation.isPending}>
            {t("CreatePartner.Dialog.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
